import React, { useEffect, useMemo, useRef, useState } from "react";
import { CreationDateSortOptions, UserColumnHeader } from "src/enums";
import { DateTimeRange, UserAccountStatus, UserRole, UsersFiltersState } from "src/types";
import { Badge } from "src/shadcn/ui/badge";
import { Button } from "src/shadcn/ui/button";
import { Input } from "src/shadcn/ui/input";
import { OptionDropDown } from "src/components/ui/inputs";
import { ChevronDown, X } from "lucide-react";
import { LoadingState } from "src/components";
import { VendorService, UserService } from "src/services";

const ROLE_OPTIONS: UserRole[] = [
  "ADMIN",
  "HEALF_OPERATIONS_MANAGER",
  "HEALF_OPERATIONS",
  "HEALF_ASSOCIATE_MANAGER",
  "HEALF_ASSOCIATE",
  "HEALF_ACCOUNTANT",
  "WAREHOUSE",
  "RELABELLING",
  "VENDOR"
];

const STATUS_OPTIONS: UserAccountStatus[] = ["Active", "Provisioned", "Deactivated"];

const TOGGLE_BUTTON_BASE =
  "px-2 py-1.5 text-xs font-medium rounded-full border transition-all duration-150 flex items-center gap-1 justify-center";

const defaultFiltersState = (): UsersFiltersState => ({
  roles: [],
  statuses: [],
  vendorName: undefined,
  jobTitle: undefined,
  supervisorEmail: undefined
});

interface FilterUsersProps {
  filters: UsersFiltersState;
  onFilterChange: (filters: UsersFiltersState) => void;
  dateTimeRange: DateTimeRange;
  onDateTimeRangeChange: (range: DateTimeRange) => void;
  sortOption: CreationDateSortOptions;
  onSortChange: (option: CreationDateSortOptions) => void;
  columnsToHide: UserColumnHeader[];
  onColumnsToHideChange: (columns: UserColumnHeader[]) => void;
  allColumns: UserColumnHeader[];
  onClose?: () => void;
  isOpen?: boolean;
  vendorService?: VendorService;
  userService?: UserService;
}

const FilterUsers: React.FC<FilterUsersProps> = ({
  filters,
  onFilterChange,
  dateTimeRange,
  onDateTimeRangeChange,
  sortOption,
  onSortChange,
  columnsToHide,
  onColumnsToHideChange,
  allColumns,
  onClose,
  isOpen,
  vendorService,
  userService
}) => {
  const [localFilters, setLocalFilters] = useState<UsersFiltersState>(filters);
  const [localDateTimeRange, setLocalDateTimeRange] = useState<DateTimeRange>(dateTimeRange);
  const [localSortOption, setLocalSortOption] = useState<CreationDateSortOptions>(sortOption);
  const [dropdownOptions, setDropdownOptions] = useState<{ vendorNames?: string[]; supervisorEmails?: string[] }>({});
  const [loadingDropdownOptions, setLoadingDropdownOptions] = useState(false);
  const [dropdownOptionsFetchError, setDropdownOptionsFetchError] = useState(false);
  const [vendorNameInput, setVendorNameInput] = useState<string>(filters.vendorName ?? "");
  const [supervisorEmailInput, setSupervisorEmailInput] = useState<string>(filters.supervisorEmail ?? "");
  const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);
  const [isSupervisorDropdownOpen, setIsSupervisorDropdownOpen] = useState(false);
  const vendorInputRef = useRef<HTMLDivElement>(null);
  const supervisorInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      setLocalDateTimeRange(dateTimeRange);
      setLocalSortOption(sortOption);
      setVendorNameInput(filters.vendorName ?? "");
      setSupervisorEmailInput(filters.supervisorEmail ?? "");
    }
  }, [isOpen, filters, dateTimeRange, sortOption]);

  const fetchDropdownOptions = async () => {
    if (!vendorService && !userService) return;
    setDropdownOptionsFetchError(false);
    setLoadingDropdownOptions(true);
    try {
      const [vendorNames, supervisorEmails] = await Promise.all([
        vendorService?.getVendorNames() ?? [],
        userService?.getUserEmails(["HEALF_OPERATIONS", "HEALF_OPERATIONS_MANAGER"]) ?? []
      ]);
      setDropdownOptions({ vendorNames, supervisorEmails });
    } catch (error) {
      console.error("Failed to load dropdown options", error);
      setDropdownOptionsFetchError(true);
    } finally {
      setLoadingDropdownOptions(false);
    }
  };

  useEffect(() => {
    void fetchDropdownOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorService, userService]);

  type ToggleableKey = "roles" | "statuses";
  type ToggleableValueMap = {
    roles: UserRole;
    statuses: UserAccountStatus;
  };

  const toggleFilterValue = <K extends ToggleableKey>(key: K, value: ToggleableValueMap[K]) => {
    setLocalFilters((prev) => {
      const currentValues = prev[key] as ToggleableValueMap[K][];
      const exists = currentValues.includes(value);
      const updatedValues = exists ? currentValues.filter((item) => item !== value) : [...currentValues, value];
      return { ...prev, [key]: updatedValues };
    });
  };

  const handleTextChange = (key: "jobTitle" | "supervisorEmail", value: string) => {
    const normalisedValue = value.trim().length ? value : undefined;
    setLocalFilters((prev) => ({ ...prev, [key]: normalisedValue }));
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    if (!value) {
      const updatedRange = { ...localDateTimeRange, [field]: undefined };
      setLocalDateTimeRange(updatedRange);
      return;
    }

    const newDate = new Date(value);
    const maxEndDate = new Date();

    if (field === "start") {
      const maxAllowedStart = localDateTimeRange.end ? new Date(localDateTimeRange.end) : undefined;
      const clampedStart = maxAllowedStart && newDate > maxAllowedStart ? maxAllowedStart : newDate;
      setLocalDateTimeRange({
        start: clampedStart,
        end: localDateTimeRange.end && clampedStart > localDateTimeRange.end ? clampedStart : localDateTimeRange.end
      });
    } else {
      const minAllowedEnd = localDateTimeRange.start ? new Date(localDateTimeRange.start) : undefined;
      let clampedEnd = newDate > maxEndDate ? maxEndDate : newDate;
      if (minAllowedEnd && clampedEnd < minAllowedEnd) {
        clampedEnd = minAllowedEnd;
      }

      setLocalDateTimeRange({
        ...localDateTimeRange,
        end: clampedEnd
      });
    }
  };

  const handleVendorSelection = (selectedVendor?: string) => {
    const trimmedValue = selectedVendor?.trim();
    setVendorNameInput(trimmedValue ?? "");
    setLocalFilters((prev) => ({ ...prev, vendorName: trimmedValue || undefined }));
  };

  const handleSupervisorEmailChange = (value: string) => {
    setSupervisorEmailInput(value);
    handleTextChange("supervisorEmail", value);
  };

  const handleApply = () => {
    onFilterChange(localFilters);
    onDateTimeRangeChange(localDateTimeRange);
    onSortChange(localSortOption);
    onClose?.();
  };

  const handleClearAll = () => {
    const clearedFilters = defaultFiltersState();
    const defaultRange = { start: undefined, end: undefined };

    setLocalFilters(clearedFilters);
    setVendorNameInput("");
    setSupervisorEmailInput("");
    setLocalDateTimeRange(defaultRange);
    setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC);

    onFilterChange(clearedFilters);
    onDateTimeRangeChange(defaultRange);
    onSortChange(CreationDateSortOptions.CREATION_DATE_DESC);
  };

  const countActiveFilters = useMemo(() => {
    let count = 0;
    if (localFilters.roles.length) count++;
    if (localFilters.statuses.length) count++;
    if (localFilters.vendorName) count++;
    if (localFilters.jobTitle?.trim()) count++;
    if (localFilters.supervisorEmail?.trim()) count++;
    if (localDateTimeRange.start) count++;
    if (localDateTimeRange.end) count++;
    if (localSortOption !== CreationDateSortOptions.CREATION_DATE_DESC) count++;
    return count;
  }, [localFilters, localDateTimeRange, localSortOption]);

  if (loadingDropdownOptions) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingState type="LOADING" />
      </div>
    );
  }

  if (dropdownOptionsFetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <p className="text-sm text-secondary-600">Failed to load filter options.</p>
        <Button onClick={fetchDropdownOptions} variant="outline" size="sm">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Filters</h3>
            {countActiveFilters > 0 && (
              <Badge variant="default" className="bg-secondary-900 text-white text-xs px-1.5 py-0 h-4">
                {countActiveFilters}
              </Badge>
            )}
          </div>
          {countActiveFilters > 0 && (
            <button
              onClick={handleClearAll}
              className="text-xs text-secondary-600 hover:text-secondary-900 transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Roles</label>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((role) => {
                const active = localFilters.roles.includes(role);
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleFilterValue("roles", role)}
                    className={`${TOGGLE_BUTTON_BASE} ${
                      active ? "bg-secondary-900 text-white border-secondary-900" : "bg-secondary-50 text-secondary-700"
                    }`}
                  >
                    {role.replace(/HEALF_/g, "").replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Statuses</label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((status) => {
                const active = localFilters.statuses.includes(status);
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => toggleFilterValue("statuses", status)}
                    className={`${TOGGLE_BUTTON_BASE} ${
                      active ? "bg-secondary-900 text-white border-secondary-900" : "bg-secondary-50 text-secondary-700"
                    }`}
                  >
                    {status}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Vendor Name</label>
            <div className="relative" ref={vendorInputRef}>
              <Input
                value={vendorNameInput}
                onChange={(e) => {
                  handleVendorSelection(e.target.value);
                  setIsVendorDropdownOpen(true);
                }}
                onFocus={() => setIsVendorDropdownOpen(true)}
                onBlur={() => setTimeout(() => setIsVendorDropdownOpen(false), 150)}
                placeholder="Search vendor name..."
                className="pr-20 text-sm h-9"
                disabled={!dropdownOptions.vendorNames?.length}
              />
              <div className="absolute right-1 top-1 flex gap-1">
                {vendorNameInput && (
                  <button
                    type="button"
                    onClick={() => handleVendorSelection(undefined)}
                    className="p-1 hover:bg-secondary-100 rounded transition-colors"
                  >
                    <X className="h-3.5 w-3.5 text-secondary-500" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsVendorDropdownOpen((prev) => !prev)}
                  className="p-1 hover:bg-secondary-100 rounded transition-colors"
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-secondary-500 transition-transform ${isVendorDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              <OptionDropDown
                value={vendorNameInput}
                onChange={(value) => {
                  handleVendorSelection(value);
                  setIsVendorDropdownOpen(false);
                }}
                isOpen={isVendorDropdownOpen}
                options={dropdownOptions.vendorNames || []}
                triggerRef={vendorInputRef}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Job Title</label>
            <Input
              value={localFilters.jobTitle ?? ""}
              onChange={(e) => handleTextChange("jobTitle", e.target.value)}
              placeholder="Search by job title..."
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Supervisor Email</label>
            <div className="relative" ref={supervisorInputRef}>
              <Input
                value={supervisorEmailInput}
                onChange={(e) => handleSupervisorEmailChange(e.target.value)}
                onFocus={() => setIsSupervisorDropdownOpen(true)}
                onBlur={() => setTimeout(() => setIsSupervisorDropdownOpen(false), 150)}
                placeholder="Type or select an email..."
                className="pr-20 text-sm h-9"
              />
              <div className="absolute right-1 top-1 flex gap-1">
                {supervisorEmailInput && (
                  <button
                    type="button"
                    onClick={() => handleSupervisorEmailChange("")}
                    className="p-1 hover:bg-secondary-100 rounded transition-colors"
                  >
                    <X className="h-3.5 w-3.5 text-secondary-500" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsSupervisorDropdownOpen((prev) => !prev)}
                  className="p-1 hover:bg-secondary-100 rounded transition-colors"
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-secondary-500 transition-transform ${isSupervisorDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              <OptionDropDown
                value={supervisorEmailInput}
                onChange={(value) => handleSupervisorEmailChange(value ?? "")}
                isOpen={isSupervisorDropdownOpen}
                options={dropdownOptions.supervisorEmails || []}
                triggerRef={supervisorInputRef}
              />
            </div>
            <p className="text-xs text-muted-foreground">You can select a suggested email or type any address.</p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Created Date</label>
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1">
                <span className="text-xs text-secondary-500">Start</span>
                <Input
                  type="datetime-local"
                  value={formatDateForInput(localDateTimeRange.start)}
                  onChange={(e) => handleDateChange("start", e.target.value)}
                  max={formatDateForInput(localDateTimeRange.end || new Date())}
                  className="text-sm h-9"
                />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-secondary-500">End</span>
                <Input
                  type="datetime-local"
                  value={formatDateForInput(localDateTimeRange.end)}
                  onChange={(e) => handleDateChange("end", e.target.value)}
                  min={localDateTimeRange.start ? formatDateForInput(localDateTimeRange.start) : undefined}
                  max={formatDateForInput(new Date())}
                  className="text-sm h-9"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_DESC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_ASC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_ASC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Oldest
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Show/Hide Columns</h3>
          <div className="flex items-center gap-2">
            {columnsToHide.length > 0 && (
              <button
                onClick={() => onColumnsToHideChange([])}
                className="text-xs text-secondary-600 hover:text-secondary-900 transition-colors"
              >
                Reset
              </button>
            )}
            <span className="text-xs text-secondary-600 font-medium">
              {allColumns.length - columnsToHide.length}/{allColumns.length}
            </span>
          </div>
        </div>
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
          {allColumns.map((column) => {
            const isVisible = !columnsToHide.includes(column);
            return (
              <button
                key={column}
                onClick={() => {
                  if (isVisible) {
                    onColumnsToHideChange([...columnsToHide, column]);
                  } else {
                    onColumnsToHideChange(columnsToHide.filter((c) => c !== column));
                  }
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all ${
                  isVisible ? "bg-secondary-50 text-secondary-900 hover:bg-secondary-100" : "text-secondary-400"
                }`}
              >
                <span className="font-medium truncate">{column}</span>
                <Badge variant={isVisible ? "default" : "outline"} className="text-xs px-1.5 py-0 h-4 ml-2">
                  {isVisible ? "Visible" : "Hidden"}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-0 pt-3 pb-1 bg-background border-t border-secondary-200 -mx-6 px-6 z-10">
        <Button
          onClick={handleApply}
          className="w-full bg-secondary-900 hover:bg-secondary-800 h-9 text-sm font-medium text-white"
        >
          Apply Changes
        </Button>
      </div>
    </div>
  );
};

export { FilterUsers };
